import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchGuests,
  fetchGuest,
  createGuest,
  updateGuest,
  fetchGuestBookings,
  addGuestNote,
  fetchGuestCommunications,
  type GuestsQuery,
  type CreateGuestPayload,
  type UpdateGuestPayload,
  type GuestCommunicationsQuery,
} from '@/services/api/guests.api';

export function useGuests(query?: GuestsQuery) {
  return useQuery({
    queryKey: ['guests', query],
    queryFn: () => fetchGuests(query),
    staleTime: 30_000,
  });
}

export function useGuest(id: string | null | undefined) {
  return useQuery({
    queryKey: ['guests', id],
    queryFn: () => fetchGuest(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useGuestBookings(id: string | null | undefined) {
  return useQuery({
    queryKey: ['guests', id, 'bookings'],
    queryFn: () => fetchGuestBookings(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateGuest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGuestPayload) => createGuest(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useUpdateGuest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateGuestPayload }) =>
      updateGuest(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useAddGuestNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      addGuestNote(id, note),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['guests', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useGuestCommunications(
  guestId: string | null | undefined,
  query?: GuestCommunicationsQuery,
) {
  return useQuery({
    queryKey: ['guests', guestId, 'communications', query],
    queryFn: () => fetchGuestCommunications(guestId!, query),
    enabled: !!guestId,
    staleTime: 30_000,
  });
}
